import {Button} from '@/components/ui/button/Button'
import {MessageChatSquareIcon} from '@/components/ui/icon/Icon'
import {useState, useCallback, useEffect, useMemo} from 'react'
import {ConversationsModal} from '../conversations-modal/ConversationsModal'
import {useSearchParams} from 'react-router-dom'

interface ConversationToggleProps {
    patientId: string
}

const CONVERSATION_ID_PARAM = 'conversation_id'

export const ConversationToggle = ({patientId}: ConversationToggleProps) => {
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [searchParams, setSearchParams] = useSearchParams()

    const conversationId = searchParams.get(CONVERSATION_ID_PARAM) ?? null

    const handleOpenModal = useCallback(() => {
        setIsModalOpen(true)
    }, [])

    const clearConversationParam = useCallback(() => {
        if (!conversationId) return
        const newSearchParams = new URLSearchParams(searchParams)
        newSearchParams.delete(CONVERSATION_ID_PARAM)
        setSearchParams(newSearchParams, {replace: true})
    }, [conversationId, searchParams, setSearchParams])

    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false)
        clearConversationParam()
    }, [clearConversationParam])

    const handleClearInitialConversation = clearConversationParam

    // Hosting the notification work-context bar. `closeSurface` deliberately skips
    // clearConversationParam: when the bar ends the context it clears this param in the same
    // navigation, and a second write here would restore it — reopening the panel just left.
    const notificationContextHost = useMemo(
        () => ({searchParam: CONVERSATION_ID_PARAM, closeSurface: () => setIsModalOpen(false)}),
        []
    )

    useEffect(() => {
        if (conversationId && !isModalOpen) {
            setIsModalOpen(true)
        }
    }, [conversationId, isModalOpen])

    return (
        <>
            <Button variant="secondary" shape="square" onClick={handleOpenModal}>
                <MessageChatSquareIcon />
            </Button>
            <ConversationsModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                patientId={patientId}
                initialConversationId={conversationId}
                onClearInitialConversation={handleClearInitialConversation}
                notificationContextHost={notificationContextHost}
            />
        </>
    )
}
